import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, Loader2, Play, SkipForward, BookOpen } from "lucide-react";
import { Footer } from "@/components/site/Footer";
import { SeriesSubscribe } from "@/components/site/SeriesSubscribe";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const mediaUrl = (src) => (src && src.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src || "");
const ytId = (url) => (url || "").match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([\w-]{11})/)?.[1] || "";

const TopBar = ({ name }) => (
  <header className="fixed top-0 left-0 right-0 z-[1000] bg-black/70 backdrop-blur-xl border-b border-white/10">
    <div className="flex items-center justify-between px-5 md:px-10 h-[68px]">
      <Link to="/" data-testid="watch-logo" className="font-display text-2xl md:text-3xl tracking-tight leading-none"><span className="text-[#E30613]">VL</span><span className="text-white">MAG</span></Link>
      <span className="hidden md:block text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-500 truncate max-w-[40vw]">{name}</span>
      <Link to="/series" data-testid="watch-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-neutral-400 hover:text-white transition-colors"><ArrowLeft size={16} /> All Series</Link>
    </div>
  </header>
);

export default function SeriesWatch() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [group, setGroup] = useState(null);
  const [status, setStatus] = useState("loading");
  const [autoplay, setAutoplay] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/series/${slug}`).then((r) => { setGroup(r.data); setStatus("ready"); }).catch(() => setStatus("error"));
  }, [slug]);

  const episodes = useMemo(() => (group?.parts || []).filter((p) => ytId(p.video_url)), [group]);
  const activeId = params.get("ep") || episodes[0]?.id;
  const active = episodes.find((e) => e.id === activeId) || episodes[0];
  const nextEp = active ? episodes[(episodes.findIndex((e) => e.id === active.id) + 1) % episodes.length] : null;

  useEffect(() => { if (group) document.title = `${group.series} — Watch on VLMAG`; }, [group]);

  const pick = (ep) => { setAutoplay(true); setParams({ ep: ep.id }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  if (status === "loading") return <div className="bg-black min-h-screen flex items-center justify-center text-neutral-500"><Loader2 className="animate-spin mr-2" size={18} /> Loading…</div>;
  if (status === "error" || !group) return (
    <div className="bg-black text-white min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="watch-not-found">
      <p className="font-display uppercase text-4xl">Series not found</p>
      <Link to="/series" className="mt-6 text-xs uppercase tracking-[0.2em] font-bold text-[#E30613]">Back to all series</Link>
    </div>
  );

  return (
    <div className="bg-black text-white min-h-screen" data-testid="series-watch">
      <TopBar name={group.series} />
      <section className="pt-[68px]">
        {active ? (
          <div className="relative bg-[#050505]">
            <div className="mx-auto max-w-[1400px] px-0 md:px-10 md:pt-8">
              <div className="aspect-video bg-black md:border md:border-white/10" data-testid="watch-player">
                <iframe
                  key={active.id}
                  title={active.title}
                  src={`https://www.youtube.com/embed/${ytId(active.video_url)}?rel=0&modestbranding=1${autoplay ? "&autoplay=1" : ""}`}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        ) : null}

        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10 md:py-14 grid lg:grid-cols-[1fr_380px] gap-10 lg:gap-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-[10px] md:text-xs uppercase tracking-[0.3em] font-bold text-[#E30613]">{group.network} · {group.series}</p>
            <h1 className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-4">{group.series}</h1>
            <p className="text-neutral-400 mt-5 max-w-2xl text-base md:text-lg leading-relaxed">
              Street-level conversations with the designers, hustlers and creatives building New York style one sidewalk at a time. {episodes.length} {episodes.length === 1 ? "session" : "sessions"} and counting.
            </p>
            {active && (
              <div className="mt-10 border-t border-white/10 pt-8" data-testid="watch-active">
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500">Now watching · Session {active.part || 1}</p>
                <h2 className="font-display uppercase text-2xl md:text-3xl leading-[0.95] tracking-tight mt-3">{active.title}</h2>
                {active.excerpt && <p className="text-neutral-400 mt-4 leading-relaxed">{active.excerpt}</p>}
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to={`/story/${active.id}`} data-testid="watch-read" className="inline-flex items-center gap-2 bg-[#E30613] text-white text-xs uppercase tracking-[0.25em] font-bold px-6 py-3.5 hover:bg-white hover:text-black transition-colors"><BookOpen size={15} /> Read the feature</Link>
                  {episodes.length > 1 && nextEp && (
                    <button onClick={() => pick(nextEp)} data-testid="watch-next" className="inline-flex items-center gap-2 border border-white/25 text-white text-xs uppercase tracking-[0.25em] font-bold px-6 py-3.5 hover:border-[#E30613] transition-colors"><SkipForward size={15} /> Next session</button>
                  )}
                </div>
              </div>
            )}
          </motion.div>

          <aside data-testid="watch-episodes">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-neutral-500 mb-4">All sessions</p>
            <ol className="border border-white/10 divide-y divide-white/10">
              {episodes.map((ep, i) => {
                const isActive = ep.id === active?.id;
                return (
                  <li key={ep.id}>
                    <button onClick={() => pick(ep)} data-testid={`watch-episode-${ep.id}`}
                      className={`group w-full text-left flex gap-4 p-4 transition-colors ${isActive ? "bg-white/[0.06]" : "hover:bg-white/[0.04]"}`}>
                      <div className="relative w-28 shrink-0 aspect-video overflow-hidden border border-white/10">
                        <img src={mediaUrl(ep.image)} alt="" className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${isActive ? "" : "grayscale"}`} />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/40"><Play size={16} fill="#fff" className="text-white" /></span>
                      </div>
                      <div className="min-w-0">
                        <p className={`text-[10px] uppercase tracking-[0.2em] font-bold ${isActive ? "text-[#E30613]" : "text-neutral-500"}`}>{isActive ? "Now playing" : `Session ${ep.part || i + 1}`}</p>
                        <p className="text-sm font-bold text-white leading-snug line-clamp-2 mt-1">{ep.title}</p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ol>
            <div className="mt-6"><SeriesSubscribe slug={group.slug} series={group.series} compact /></div>
            <Link to="/series" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-neutral-400 hover:text-white transition-colors">More series <ArrowUpRight size={14} /></Link>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
}
